// @ts-nocheck
// --- YouTube IFrame API shared loader ---
const _ytQueue = [];
let _ytReady = false;

function ensureYouTubeAPI(cb) {
  if (_ytReady && window.YT?.Player) { cb(); return; }
  _ytQueue.push(cb);
  if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
    const s = document.createElement('script');
    s.src = 'https://www.youtube.com/iframe_api';
    document.head.appendChild(s);
  }
  if (!window._novaYTPatched) {
    window._novaYTPatched = true;
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      _ytReady = true;
      prev?.();
      _ytQueue.splice(0).forEach(fn => fn());
    };
  }
}

// --- Vimeo SDK shared loader ---
const _vimeoQueue = [];
let _vimeoReady = false;

function ensureVimeoAPI(cb) {
  if (_vimeoReady && window.Vimeo?.Player) { cb(); return; }
  _vimeoQueue.push(cb);
  if (!document.querySelector('script[src*="player.vimeo.com/api/player"]')) {
    const s = document.createElement('script');
    s.src = 'https://player.vimeo.com/api/player.js';
    s.onload = () => {
      _vimeoReady = true;
      _vimeoQueue.splice(0).forEach(fn => fn());
    };
    document.head.appendChild(s);
  }
}

// --- Custom Element ---
class NovaVideoControls extends HTMLElement {
  connectedCallback() {
    this._savedVolume   = 1;
    this._videoType     = this.dataset.videoType || 'native';

    this._playPauseBtn = this.querySelector('[data-nova-vc-action="playpause"]');
    this._muteBtn      = this.querySelector('[data-nova-vc-action="mute"]');
    this._volumeSlider = this.querySelector('.nova-vc__volume');
    this._audioGroup   = this.querySelector('.nova-vc__audio-group');

    const dm = this.closest('deferred-media');
    if (!dm) return;

    this._startMuted  = dm.hasAttribute('autoplay');
    this._initialized = false;
    this._pendingPlay = false;

    if (this._startMuted) {
      // Autoplay: video already loading — detect player after it's in DOM.
      setTimeout(() => { this._detect(dm); this._show(); }, 250);
    } else {
      // No autoplay: show controls immediately; they are the only play/pause mechanism.
      this._updatePlay(false);
      this._updateMute(false);
      this._show();

      const posterBtn = dm.querySelector('.deferred-media__poster-button');
      if (!posterBtn) {
        // No poster: video already in DOM (URL embed, no cover image). Detect and init.
        // Edge case: user may click play before the 250ms timeout; flag it so _init* auto-plays.
        this._playPauseBtn?.addEventListener('click', () => {
          if (!this._initialized) this._pendingPlay = true;
        }, { once: true });
        setTimeout(() => this._detect(dm), 250);
      } else {
        // Video in <template>: init player once Shopify loads it after poster interaction.
        new MutationObserver((_, obs) => {
          if (posterBtn.classList.contains('deferred-media__playing')) {
            obs.disconnect();
            // No custom play button: poster click is the only start mechanism, auto-play after init.
            if (!this._playPauseBtn) this._pendingPlay = true;
            setTimeout(() => this._detect(dm), 250);
          }
        }).observe(posterBtn, { attributes: true, attributeFilter: ['class'] });

        // Play button triggers deferred-media loading on first click.
        // Guard: skip posterBtn.click() if the user already clicked the poster directly.
        this._playPauseBtn?.addEventListener('click', (e) => {
          e.stopPropagation();
          if (!this._initialized) {
            this._pendingPlay = true;
            if (!posterBtn.classList.contains('deferred-media__playing')) posterBtn.click();
          }
        }, { once: true });
      }
    }
  }

  _detect(dm) {
    const iframe = dm.querySelector('iframe');
    const video  = dm.querySelector('video');
    if (iframe) {
      const type = iframe.dataset.videoType || this._videoType;
      if (type === 'youtube') { this._initYT(iframe); return; }
      if (type === 'vimeo')   { this._initVimeo(iframe); return; }
    }
    if (video) this._initNative(video);
  }

  _show() {
    this.classList.add('nova-vc--visible');
  }

  _updatePlay(playing) {
    const btn = this._playPauseBtn;
    if (!btn) return;
    btn.querySelector('.nova-vc__icon--play')?.classList.toggle('nova-vc__icon--hidden', playing);
    btn.querySelector('.nova-vc__icon--pause')?.classList.toggle('nova-vc__icon--hidden', !playing);
    btn.setAttribute('aria-label', playing
      ? (btn.dataset.labelPause || 'Pause')
      : (btn.dataset.labelPlay  || 'Play'));
  }

  _updateMute(muted) {
    const btn = this._muteBtn;
    if (!btn) return;
    btn.querySelector('.nova-vc__icon--sound-on')?.classList.toggle('nova-vc__icon--hidden', muted);
    btn.querySelector('.nova-vc__icon--sound-off')?.classList.toggle('nova-vc__icon--hidden', !muted);
    btn.setAttribute('aria-label', muted
      ? (btn.dataset.labelUnmute || 'Unmute')
      : (btn.dataset.labelMute   || 'Mute'));
  }

  disconnectedCallback() {
    if (this._fsHandler) document.removeEventListener('fullscreenchange', this._fsHandler);
    this._ioObserver?.disconnect();
  }

  _setupIO(target, play, pause) {
    if (!this._startMuted) return;
    this._ioObserver = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (e.intersectionRatio >= 0.2) play();
        else if (e.intersectionRatio === 0) pause();
      });
    }, { threshold: [0, 0.2] });
    this._ioObserver.observe(target);
  }

  // ---- Native <video> ----
  _initNative(video) {
    this._initialized = true;
    // No HTML attribute prevents double-click fullscreen; intercept it here.
    video.addEventListener('dblclick', e => e.preventDefault());
    this._fsHandler = () => { if (document.fullscreenElement === video) document.exitFullscreen(); };
    document.addEventListener('fullscreenchange', this._fsHandler);

    const sliderVol = this._volumeSlider ? parseFloat(this._volumeSlider.value) : 1;
    if (this._startMuted) {
      video.muted = true;
      video.volume = sliderVol;
      this._savedVolume = sliderVol;
      if (this._volumeSlider) this._volumeSlider.value = 0;
      this._updateMute(true);
    } else {
      video.volume = sliderVol;
      this._updateMute(video.muted || sliderVol === 0);
    }
    this._updatePlay(!video.paused);
    if (this._pendingPlay) { this._pendingPlay = false; if (video.paused) video.play(); }

    this._playPauseBtn?.addEventListener('click', e => {
      e.stopPropagation();
      video.paused ? video.play() : video.pause();
    });
    video.addEventListener('play',  () => this._updatePlay(true));
    video.addEventListener('pause', () => this._updatePlay(false));

    this._volumeSlider?.addEventListener('input', e => {
      e.stopPropagation();
      const vol = parseFloat(e.target.value);
      video.volume = vol;
      this._updateVolumeFill(vol);
      if (vol > 0 && video.muted) {
        video.muted = false;
        this._updateMute(false);
      } else if (vol === 0 && !video.muted) {
        video.muted = true;
        this._updateMute(true);
      }
    });

    this._muteBtn?.addEventListener('click', e => {
      e.stopPropagation();
      if (!video.muted && video.volume > 0) {
        this._savedVolume = video.volume;
        video.muted = true;
        if (this._volumeSlider) {
          this._volumeSlider.value = 0;
          this._updateVolumeFill(0);
        }
        this._updateMute(true);
      } else {
        const restore = this._savedVolume > 0 ? this._savedVolume : 1;
        video.muted = false;
        video.volume = restore;
        if (this._volumeSlider) {
          this._volumeSlider.value = restore;
          this._updateVolumeFill(restore);
        }
        this._updateMute(false);
      }
    });

    this._detectAudio(video);
    this._setupIO(this.closest('deferred-media') || this, () => video.play().catch(() => {}), () => video.pause());
  }

  _showAudioUI() {
    if (this._audioGroup) {
      this._audioGroup.style.display = '';
      this._updateVolumeFill(this._volumeSlider ? parseFloat(this._volumeSlider.value) : 1);
    }
  }

  _updateVolumeFill(vol) {
    this._volumeSlider?.style.setProperty('--nova-vol-pct', (vol * 100) + '%');
  }

  _detectAudio(video) {
    // Firefox / Safari: audioTracks length is accurate once metadata is loaded
    if (video.audioTracks !== undefined) {
      const check = () => { if (video.audioTracks.length > 0) this._showAudioUI(); };
      if (video.readyState >= HTMLMediaElement.HAVE_METADATA) {
        check();
      } else {
        video.addEventListener('loadedmetadata', check, { once: true });
      }
      return;
    }

    // Chrome: webkitAudioDecodedByteCount > 0 after first timeupdate means audio present
    if (typeof video.webkitAudioDecodedByteCount !== 'undefined') {
      const check = () => {
        if (video.webkitAudioDecodedByteCount > 0) this._showAudioUI();
      };
      if (!video.paused) {
        video.addEventListener('timeupdate', check, { once: true });
      } else {
        video.addEventListener('playing', () => {
          video.addEventListener('timeupdate', check, { once: true });
        }, { once: true });
      }
      return;
    }

    // Other browsers: no reliable detection — show conservatively
    this._showAudioUI();
  }

  // ---- YouTube IFrame API ----
  _initYT(iframe) {
    this._initialized = true;
    if (!iframe.id) iframe.id = 'nova-yt-' + Math.random().toString(36).slice(2, 9);
    ensureYouTubeAPI(() => {
      new YT.Player(iframe.id, {
        events: {
          onReady: e => {
            const p = e.target;
            this._ytPlayer = p;
            const sliderVol = this._volumeSlider ? parseFloat(this._volumeSlider.value) : 1;
            if (this._startMuted && this._volumeSlider) this._volumeSlider.value = 0;
            this._showAudioUI();
            if (this._startMuted) {
              p.setVolume(sliderVol * 100);
              p.mute();
              this._savedVolume = sliderVol;
              this._updateMute(true);
            } else {
              p.setVolume(sliderVol * 100);
              sliderVol === 0 ? p.mute() : p.unMute();
              // Use intended state — p.isMuted() may still report true before YouTube
              // processes the unMute() command, causing the icon to flip incorrectly.
              this._updateMute(sliderVol === 0);
            }
            this._updatePlay(p.getPlayerState() === YT.PlayerState.PLAYING);
            if (this._pendingPlay) {
              this._pendingPlay = false;
              if (p.getPlayerState() !== YT.PlayerState.PLAYING) p.playVideo();
            }

            this._playPauseBtn?.addEventListener('click', ev => {
              ev.stopPropagation();
              p.getPlayerState() === YT.PlayerState.PLAYING ? p.pauseVideo() : p.playVideo();
            });
            this._volumeSlider?.addEventListener('input', ev => {
              ev.stopPropagation();
              const vol = parseFloat(ev.target.value);
              p.setVolume(vol * 100);
              this._updateVolumeFill(vol);
              if (vol > 0 && p.isMuted()) {
                p.unMute();
                this._updateMute(false);
              } else if (vol === 0) {
                p.mute();
                this._updateMute(true);
              }
            });
            this._muteBtn?.addEventListener('click', ev => {
              ev.stopPropagation();
              if (p.isMuted() || p.getVolume() === 0) {
                const restore = this._savedVolume > 0 ? this._savedVolume : 1;
                p.unMute();
                p.setVolume(restore * 100);
                if (this._volumeSlider) {
                  this._volumeSlider.value = restore;
                  this._updateVolumeFill(restore);
                }
                this._updateMute(false);
              } else {
                this._savedVolume = p.getVolume() / 100;
                p.mute();
                if (this._volumeSlider) {
                  this._volumeSlider.value = 0;
                  this._updateVolumeFill(0);
                }
                this._updateMute(true);
              }
            });
          },
          onStateChange: e => this._updatePlay(e.data === YT.PlayerState.PLAYING)
        }
      });
      this._setupIO(
        this.closest('deferred-media') || this,
        () => this._ytPlayer?.playVideo(),
        () => this._ytPlayer?.pauseVideo()
      );
    });
  }

  // ---- Vimeo Player SDK ----
  _initVimeo(iframe) {
    this._initialized = true;
    ensureVimeoAPI(() => {
      const p = new Vimeo.Player(iframe);
      const sliderVol = this._volumeSlider ? parseFloat(this._volumeSlider.value) : 1;
      if (this._startMuted && this._volumeSlider) this._volumeSlider.value = 0;
      this._showAudioUI();
      if (this._startMuted) {
        p.setVolume(0);
        this._savedVolume = sliderVol;
        this._updateMute(true);
      } else {
        p.setVolume(sliderVol);
        this._updateMute(sliderVol === 0);
      }
      p.getPaused().then(paused => {
        this._updatePlay(!paused);
        if (this._pendingPlay) { this._pendingPlay = false; if (paused) p.play(); }
      }).catch(() => {});

      this._playPauseBtn?.addEventListener('click', ev => {
        ev.stopPropagation();
        p.getPaused().then(paused => paused ? p.play() : p.pause());
      });
      p.on('play',  () => this._updatePlay(true));
      p.on('pause', () => this._updatePlay(false));
      this._setupIO(
        this.closest('deferred-media') || this,
        () => p.play().catch(() => {}),
        () => p.pause().catch(() => {})
      );

      this._volumeSlider?.addEventListener('input', ev => {
        ev.stopPropagation();
        const vol = parseFloat(ev.target.value);
        p.setVolume(vol);
        this._updateVolumeFill(vol);
        this._updateMute(vol === 0);
      });

      this._muteBtn?.addEventListener('click', ev => {
        ev.stopPropagation();
        p.getVolume().then(vol => {
          if (vol > 0) {
            this._savedVolume = vol;
            p.setVolume(0);
            if (this._volumeSlider) {
              this._volumeSlider.value = 0;
              this._updateVolumeFill(0);
            }
            this._updateMute(true);
          } else {
            const restore = this._savedVolume > 0 ? this._savedVolume : 1;
            p.setVolume(restore);
            if (this._volumeSlider) {
              this._volumeSlider.value = restore;
              this._updateVolumeFill(restore);
            }
            this._updateMute(false);
          }
        });
      });
    });
  }
}

if (!customElements.get('nova-video-controls')) {
  customElements.define('nova-video-controls', NovaVideoControls);
}
